<template>
  <div>
    <div v-if="loggedUserCanManageUsers()" class="FirstConnectionView-cards">
      <div v-if="isLoadingDemandes">
        <BookmarkCard :is-loading="true" :tag-type="tagTypes.VALIDATION" icon-name="user" />
      </div>
      <div v-for="user in demandesEnCours" v-else :key="user.id">
        <GraeMemberDemandeCard
          v-if="canAccept(user)"
          :user-demande="user"
          :reject="reject"
          :accept="accept"
        />
      </div>
    </div>

    <TableFilterView
      :columns="columns"
      :initial-filters="activeFilters"
      page="memberList"
      @update-active-filters="updateActiveFilters"
      @update-visible-columns="updateVisibleColumns"
      @export-csv="exportCSV"
      @export-xls="exportXLS"
    />
    <b-table
      v-if="!hidden"
      :ref="tableRef"
      :striped="config.isStriped"
      :hoverable="config.isHoverable"
      :debounce-search="config.searchDebounce"
      :aria-next-label="config.nextLabel()"
      :aria-previous-label="config.previousLabel()"
      :aria-page-label="config.pageLabel()"
      :aria-current-label="config.currentPageLabel()"
      :per-page="config.perPage"
      :sticky-header="config.stickyHeader"
      :current-page="currentPagedSortedParameter.pageNumber"
      :pagination-position="config.paginationPosition"
      paginated
      backend-pagination
      :sort-icon="config.sortIcon"
      :sort-icon-size="config.sortIconSize"
      backend-sorting
      sort-multiple
      :sort-multiple-data="buefySortingPriority"
      :total="total"
      :data="memberList"
      :loading="isLoading"
      @sort="onSort"
      @sorting-priority-removed="sortingPriorityRemoved"
      @page-change="onPageChange"
    >
      <b-table-column
        v-for="column in columns"
        :key="column.field"
        v-bind="column"
        :visible="column.visible"
      >
        <template #default="props">
          <div v-if="column.field === 'profil'">
            <b-select
              v-if="canPromote(props.row) || canDemote(props.row)"
              :ref="`select-${props.row.id}`"
              v-model="props.row.profil"
              :placeholder="$t('usersList.select-profile')"
              @change="changeUserProfile(props.row, $event)"
            >
              <option value="ACCOMPAGNATEUR_REGIONAL">
                {{ $t("ACCOMPAGNATEUR_REGIONAL") }}
              </option>
              <option value="ANIMATEUR_REGIONAL">
                {{ $t("ANIMATEUR_REGIONAL") }}
              </option>
            </b-select>

            <span v-else> {{ $t(props.row.profil) }} </span>
          </div>

          <div v-if="column.field == statusColumnField">
            <div v-if="canAccept(props.row)">
              <b-select
                v-if="userBeingValidated != props.row['id'] && userBeingRefused != props.row['id']"
                :placeholder="$t('usersList.waiting')"
                @update:model-value="(value) => changeStatut(value, props.row)"
              >
                <option :value="statut.valide">
                  {{ statut.valide }}
                </option>
                <option :value="statut.refuse">
                  {{ statut.refuse }}
                </option>
              </b-select>
              <span v-else-if="userBeingValidated == props.row['id']">
                {{ statut.validated }}
              </span>
              <span v-else>
                {{ statut.refused }}
              </span>
            </div>
            <div v-else>
              {{ $t(props.row["statutActivation"]) }}
            </div>
          </div>

          <div v-if="column.field === 'action' && canDeleteUser">
            <b-button
              type="is-danger is-outlined"
              icon-left="trash"
              :title="$t('usersList.supprimer')"
              @click="deleteUser($event, props.row)"
            >
            </b-button>
            <b-button
              v-if="canValidateEmail(props.row)"
              style="margin-left: 10px"
              type="is-primary is-outlined"
              icon-left="check"
              :title="$t('usersList.validate-email')"
              @click="validateEmail($event, props.row)"
            >
            </b-button>
          </div>

          <div
            v-if="!column.custom"
            class="clickable"
            @click="showUserDetailsPage($event, props.row['id'])"
          >
            <div>{{ props.row[column.field] }}</div>
          </div>
        </template>
      </b-table-column>
    </b-table>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue"
import {
  FilterDTO,
  PaginationOrder,
  ProfilUtilisateur,
  UtilisateurDTO,
} from "@/model/bean/GeneratedDTOs"
import { GraeService } from "@/services/rest/grae/GraeService"
import { InfoReportingService } from "@/services/log/InfoReportingService"
import { UsersService } from "@/services/rest/users/UsersService"
import TableFilterView from "@/views/tables/TableFilterView.vue"
import Column from "@/views/tables/Column"
import GraeMemberDemandeCard from "./GraeMemberDemandeCard.vue"
import BookmarkCard from "@/views/generic_components/BookmarkCard.vue"
import {
  EnumHelper,
  TAG_TYPES_FOR_BOOKMARK_CARD,
  PROFIL_UTILISATEUR,
} from "@/model/bean/EnumHelper"
import { OrganismeService } from "@/services/rest/organisme/OrganismeService"
import { useTableView } from "@/hooks/useTableView"
import { SortOrder } from "@/model/SortOrder"
import { eventBus } from "@/services/EventBus"
import { BButton, BTable, BTableColumn, BSelect } from "buefy"

export default defineComponent({
  name: "GraeMembers",
  components: {
    TableFilterView,
    GraeMemberDemandeCard,
    BookmarkCard,
    BButton,
    BSelect,
    BTable,
    BTableColumn,
  },
  props: {
    graeId: {
      type: String,
      required: false,
      default: "",
    },
    loggedUser: {
      type: Object as PropType<UtilisateurDTO>,
      required: false,
      default: () => new UtilisateurDTO(),
    },
    readonly: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      graeService: GraeService.INSTANCE,
      userService: UsersService.INSTANCE,
      infoReportingService: InfoReportingService.INSTANCE,
      organismeService: OrganismeService.INSTANCE,

      memberList: [] as Array<UtilisateurDTO>,
      originalMemberList: [] as Array<UtilisateurDTO>, // Copie des données originales
      demandesEnCours: [] as Array<UtilisateurDTO>,
      userBeingValidated: "" as string | undefined,
      userBeingRefused: "" as string | undefined,
      isLoadingDemandes: false,
      canDeleteUser: false,
      // Hook sera initialisé dans created()
      tableView: null as any,
      tagTypes: TAG_TYPES_FOR_BOOKMARK_CARD,
      hidden: false,
    }
  },
  computed: {
    // Propriétés réactives du tableau
    tableRef(): string {
      return this.tableView?.state.tableRef || ""
    },

    isLoading: {
      get(): boolean {
        return this.tableView?.state.isLoading || false
      },
      set(value: boolean): void {
        if (this.tableView) {
          this.tableView.state.isLoading = value
        }
      },
    },

    columns(): Array<Column> {
      return this.tableView?.state.columns || []
    },

    currentPagedSortedParameter(): any {
      return this.tableView?.state.currentPagedSortedParameter || null
    },

    buefySortingPriority(): Array<any> {
      return this.tableView?.state.buefySortingPriority || []
    },

    total: {
      get(): number {
        return this.tableView?.state.total || 0
      },
      set(value: number): void {
        if (this.tableView) {
          this.tableView.state.total = value
        }
      },
    },

    config(): any {
      return this.tableView?.state.config || null
    },

    perPage(): number {
      return this.tableView?.state.perPage || 20
    },

    activeFilters: {
      get(): Array<any> {
        return this.tableView?.state.activeFilters || []
      },
      set(value: Array<any>): void {
        if (this.tableView) {
          this.tableView.state.activeFilters = value
        }
      },
    },

    statut(): any {
      return {
        valide: this.$t("usersList.validate").toString(),
        refuse: this.$t("usersList.refuse").toString(),
        validated: this.$t("usersList.validated").toString(),
        refused: this.$t("usersList.refused").toString(),
      }
    },

    statusColumnField(): string {
      return "statutActivation"
    },
  },
  async created(): Promise<void> {
    // Initialisation du hook useTableView
    this.tableView = useTableView({
      tableRef: "grae-members-table",
      router: this.$router,
      route: this.$route,
      i18n: this.$i18n,
      getInitialColumns: this.getInitialColumns.bind(this),
      getDefaultSort: this.getDefaultSort.bind(this),
      getInitialActiveFilters: this.getInitialActiveFilters.bind(this),
      reloadData: this.reloadData.bind(this),
      reloadDataWhenMounted: true,
      updateLocalStorageHistoryPagination: this.updateLocalStorageHistoryPagination.bind(this),
    })

    await this.loadDemandes()
    this.canDeleteUser = this.loggedUserCanManageUsers()
  },
  mounted(): void {
    this.tableView?.initMounted()
  },

  methods: {
    // Réexposer les méthodes du tableau
    updateVisibleColumns(newVisibleColumnList: Array<Column>): void {
      this.tableView?.updateVisibleColumns(newVisibleColumnList)
    },

    updateActiveFilters(newActiveFilters: Array<FilterDTO>): void {
      this.tableView?.updateActiveFilters(newActiveFilters)
    },

    onPageChange(newPage: string | number): void {
      const pageNum = typeof newPage === "string" ? parseInt(newPage) : newPage
      this.tableView?.onPageChange(pageNum)
    },

    onSort(field: string | undefined, order: SortOrder): void {
      if (field) {
        this.tableView?.onSort(field, order)
      }
    },

    sortingPriorityRemoved(field: string | undefined): void {
      if (field) {
        this.tableView?.sortingPriorityRemoved(field)
      }
    },

    updateLocalStorageHistoryPagination(): void {
      // No implementation needed as we don't store pagination for this view
    },

    updateTotal(total: number): void {
      this.tableView?.updateTotal(total)
    },

    getInitialActiveFilters(): Array<FilterDTO> {
      return new Array<FilterDTO>()
    },

    async reloadData(): Promise<void> {
      try {
        const paginatedGraeMembersList = await this.graeService.getMembers(
          this.graeId,
          this.currentPagedSortedParameter,
          this.activeFilters,
        )
        this.memberList = paginatedGraeMembersList.elements
        // Créer une copie profonde des données originales
        this.originalMemberList = JSON.parse(JSON.stringify(paginatedGraeMembersList.elements))
        this.updateTotal(paginatedGraeMembersList.count)
      } catch (e: any) {
        // Silent catch
      }
    },

    accept(user: UtilisateurDTO): void {
      this.infoReportingService.dialog(
        this.$t("usersList.confirmation").toString(),
        this.$t("usersList.are-you-sure-validate").toString(),
        this.$t("usersList.validate").toString(),
        () => this.validateUserRegistration(user),
        "is-info",
      )
    },

    validateUserRegistration(user: UtilisateurDTO): void {
      this.userBeingValidated = user.id
      this.userService.validateUserRegistration(user).then(
        (_success) => {
          this.loadDemandes()
          this.reloadData()
          this.userBeingValidated = ""
        },
        (reject) => {
          this.userBeingValidated = ""
          console.error("Could not validate user ", reject)
          this.infoReportingService.error(this.$t("userActions.cannot-validate").toString(), reject)
        },
      )
    },

    reject(user: UtilisateurDTO): void {
      this.infoReportingService.dialog(
        this.$t("usersList.confirmation").toString(),
        this.$t("usersList.are-you-sure-deny").toString(),
        this.$t("usersList.refuse").toString(),
        () => {
          this.rejectUserRegistration(user)
        },
        "is-danger",
      )
    },

    rejectUserRegistration(user: UtilisateurDTO): void {
      this.userBeingRefused = user.id
      this.userService.rejectUserRegistration(user).then(
        (_success) => {
          this.loadDemandes()
          this.reloadData()
          this.userBeingRefused = ""
        },
        (reject) => {
          this.userBeingRefused = ""
          this.infoReportingService.error(this.$t("userActions.cannot-reject").toString(), reject)
        },
      )
    },

    loggedUserCanManageUsers(): boolean {
      return this.userService.hasAnyProfile(this.loggedUser, [
        PROFIL_UTILISATEUR.ACCOMPAGNATEUR_NATIONAL,
        PROFIL_UTILISATEUR.ANIMATEUR_REGIONAL,
        PROFIL_UTILISATEUR.SUPER_ADMINISTRATEUR,
      ])
    },

    canPromote(user: UtilisateurDTO): boolean {
      return (
        this.loggedUserCanManageUsers() &&
        this.userService.hasAnyProfile(user, [PROFIL_UTILISATEUR.ACCOMPAGNATEUR_REGIONAL]) &&
        this.loggedUser.id !== user.id &&
        user.statutActivation == "ACTIF"
      )
    },

    promote(user: UtilisateurDTO): void {
      this.userService.promoteUser(user).then(
        async (_success) => {
          this.hidden = true
          this.$nextTick(async () => {
            await this.reloadData()
            this.hidden = false
          })
        },
        (reject) => {
          this.infoReportingService.error(this.$t("userActions.cannot-promote").toString(), reject)
          this.updateSelectValue(user, "ACCOMPAGNATEUR_REGIONAL")
        },
      )
    },

    updateSelectValue(user: UtilisateurDTO, value: string): void {
      const elements = this.$refs[`select-${user.id}`] as any[]

      if (!elements || elements.length === 0) {
        return
      }

      const select = elements[0]
      //@ts-ignore
      select.computedValue = value
    },

    canDemote(user: UtilisateurDTO): boolean {
      return (
        this.loggedUserCanManageUsers() &&
        this.userService.hasAnyProfile(user, [PROFIL_UTILISATEUR.ANIMATEUR_REGIONAL]) &&
        this.loggedUser.id !== user.id &&
        user.statutActivation == "ACTIF"
      )
    },

    demote(user: UtilisateurDTO): void {
      this.userService.demoteUser(user).then(
        (_success) => {
          this.reloadData()
          this.hidden = true
          this.$nextTick(async () => {
            await this.reloadData()
            this.hidden = false
          })
        },
        (reject) => {
          this.infoReportingService.error(this.$t("userActions.cannot-demote").toString(), reject)
          this.updateSelectValue(user, "ANIMATEUR_REGIONAL")
        },
      )
    },

    changeUserProfile(user: UtilisateurDTO, event: Event | ProfilUtilisateur): void {
      // Extraire la valeur du profil depuis l'événement ou utiliser directement si c'est déjà un profil
      const nouveauProfil =
        typeof event === "string"
          ? event
          : ((event.target as HTMLSelectElement).value as ProfilUtilisateur)

      // Trouver l'utilisateur original dans originalMemberList pour avoir l'ancien profil
      const originalUser = this.originalMemberList.find((u) => u.id === user.id)
      const ancienProfil = originalUser ? originalUser.profil : user.profil

      this.$nextTick(() => {
        if (nouveauProfil == "ACCOMPAGNATEUR_REGIONAL" && ancienProfil != nouveauProfil) {
          if (this.loggedUser && this.loggedUser.id === user.id) {
            this.infoReportingService.dialog(
              this.$t("usersList.confirmation").toString(),
              this.$t("usersList.are-you-sure-demote").toString(),
              this.$t("usersList.demote").toString(),
              () => this.demote(user),

              "is-warning",
            )
            //If user has clicked on cancel button :
            this.updateSelectValue(user, "ANIMATEUR_REGIONAL")
          } else {
            this.demote(user)
          }
        }

        if (nouveauProfil == "ANIMATEUR_REGIONAL" && ancienProfil != nouveauProfil) {
          this.promote(user)
        }
      })
    },

    canAccept(user: UtilisateurDTO): boolean {
      const hasExpectedProfil =
        [
          "SUPER_ADMINISTRATEUR",
          "ACCOMPAGNATEUR_NATIONAL",
          "ACCOMPAGNATEUR_REGIONAL",
          "ANIMATEUR_REGIONAL",
        ].indexOf(user.profil) >= 0
      return (
        this.loggedUserCanManageUsers() &&
        hasExpectedProfil &&
        user.statutActivation == "INSCRIPTION_A_VALIDER"
      )
    },

    changeStatut(statutValue: string, user: UtilisateurDTO): void {
      if (statutValue === this.statut.valide) {
        this.accept(user)
      } else {
        this.reject(user)
      }
    },

    deleteUser(event: Event, user: UtilisateurDTO): void {
      event.stopPropagation()
      let message = this.$t("usersList.are-you-sure-delete").toString()
      let type = "is-warning"
      if (user.hasAireEducative) {
        type = "is-danger"
        message = this.$t("usersList.delete-user-warning").toString()
      }
      this.infoReportingService.dialog(
        this.$t("usersList.confirmation").toString(),
        message,
        this.$t("usersList.delete").toString(),
        () => {
          this.deleteUserAccount(user)
        },
        type,
      )
    },

    deleteUserAccount(user: UtilisateurDTO): void {
      this.userService.deleteUserAccount(user).then(
        (_success) => {
          this.infoReportingService.success(this.$t("usersList.user-deleted").toString())
          this.reloadData()
        },
        (reject) => {
          this.infoReportingService.error(this.$t("usersList.cannot-delete").toString(), reject)
        },
      )
    },

    canValidateEmail(user: UtilisateurDTO): boolean {
      return (
        this.loggedUserCanManageUsers() &&
        user.statutActivation == "EMAIL_A_VALIDER" &&
        user.nom != "N/A"
      )
    },

    validateEmail(event: Event, user: UtilisateurDTO): void {
      event.stopPropagation()
      const message = this.$t("usersList.are-you-sure-validate-email").toString()
      const type = "is-warning"
      this.infoReportingService.dialog(
        this.$t("usersList.confirmation").toString(),
        message,
        this.$t("usersList.validate-email").toString(),
        () => {
          this.userService.validateEmail(user).then(
            (_success) => {
              this.infoReportingService.success(
                this.$t("usersList.user-email-validated").toString(),
              )
              this.reloadData()
            },
            (reject) => {
              this.infoReportingService.error(
                this.$t("usersList.cannot-validate-email").toString(),
                reject,
              )
            },
          )
        },
        type,
      )
    },

    getInitialColumns(): Array<Column> {
      return [
        {
          field: "prenom",
          label: this.$t("graePage.membres.prenom").toString(),
          sortable: true,
          backEndSearchable: true,
          visible: true,
          custom: false,
          canBeAdded: true,
        },
        {
          field: "nom",
          label: this.$t("graePage.membres.nom").toString(),
          sortable: true,
          backEndSearchable: true,
          visible: true,
          custom: false,
          canBeAdded: true,
        },
        {
          field: "profil",
          label: this.$t("usersList.profil").toString(),
          tooltip: this.$t("usersList.profil-explanations").toString(),
          sortable: true,
          backEndSearchable: true,
          possibleSearchValues: EnumHelper.allProfilsUtilisateur,
          visible: true,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "telephone",
          label: this.$t("graePage.membres.telephone").toString(),
          sortable: true,
          backEndSearchable: true,
          visible: true,
          custom: false,
          canBeAdded: true,
        },
        {
          field: "courriel",
          label: this.$t("graePage.membres.courriel").toString(),
          sortable: true,
          backEndSearchable: true,
          visible: true,
          custom: false,
          canBeAdded: true,
        },
        {
          field: "statutActivation",
          label: this.$t("usersList.statut").toString(),
          tooltip: this.$t("usersList.statut-explanations").toString(),
          sortable: true,
          backEndSearchable: true,
          possibleSearchValues: EnumHelper.allStatusActivation,
          visible: true,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "action",
          label: this.$t("projetPage.actions").toString(),
          sortable: false,
          backEndSearchable: false,
          visible: true,
          custom: true,
          canBeAdded: true,
        },
      ]
    },

    async loadDemandes(): Promise<void> {
      if (this.loggedUserCanManageUsers()) {
        this.isLoadingDemandes = true
        try {
          this.demandesEnCours = await this.graeService.getDemandesCreationCompte(this.graeId)
        } catch (e: any) {
          // Silent catch
        }
      }

      this.isLoadingDemandes = false
    },

    getDefaultSort(): Array<PaginationOrder> {
      const nameSort = new PaginationOrder()
      nameSort.clause = "nom"
      nameSort.desc = true
      const profilSort = new PaginationOrder()
      profilSort.clause = "profil"
      profilSort.desc = true
      return [profilSort, nameSort]
    },

    showUserDetailsPage(event: Event, id: number): void {
      // Do not foward click event to row (would trigger modal)
      event.stopPropagation()
      //For APP to open ProfileModale
      eventBus.emit("open-profile-modale", { show: true, id: id.toString() })
    },

    exportCSV(): void {
      this.organismeService.exportGraeMembersCSV(this.graeId, this.activeFilters)
    },

    exportXLS(): void {
      this.organismeService.exportGraeMembersXLS(this.graeId, this.activeFilters)
    },
  },
})
</script>
